import React, { FC, ReactNode, useCallback, useState } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import { EntityType } from "@mapmycustomers/shared";
import { AnyEntity, Trip } from "@mapmycustomers/shared/types/entity";
import DateRange from "@mapmycustomers/shared/types/range/DateRange";
import ViewState from "@mapmycustomers/shared/types/viewModel/ViewState";

import ExtendedAnalytics from "@app/component/analytics/ExtendedAnalytics";
import EditableNoteCellRenderer from "@app/component/grid/gridComponents/cellRenderers/EditableNoteCellRenderer";
import TripDateCellRenderer from "@app/component/grid/gridComponents/cellRenderers/TripDateCellRenderer";
import TripLocationCellRenderer from "@app/component/grid/gridComponents/cellRenderers/TripLocationCellRenderer";
import TripTypeCellRenderer from "@app/component/grid/gridComponents/cellRenderers/TripTypeCellRenderer";
import UserChipCellRenderer from "@app/component/grid/gridComponents/cellRenderers/UserChipCellRenderer";
import { getDatesRange } from "@app/enum/dashboard/dateRanges";
import {
  DrillDownModal,
  DrillDownModalProps,
} from "@app/scene/dashboard/components/cards/common/DrillDownModal";
import {
  exportMileageTrackingCardDrillDownData,
  fetchMileageTrackingCardDrillDownData,
  updateTrip,
} from "@app/scene/dashboard/store/cards/mileageTracking/actions";
import {
  getMileageTrackingDrillDownCustomDateRange,
  getMileageTrackingDrillDownTotalFilteredRecords,
  getMileageTrackingDrillDownViewState,
  isMileageTrackingDrillDownLoading,
} from "@app/scene/dashboard/store/cards/mileageTracking/selectors";
import { DrillDownListFetcherPayload } from "@app/scene/dashboard/types/DrillDownListFetcher";
import formatDrillDownDateRange from "@app/scene/dashboard/utils/formatDrillDownDateRange";
import TripPreview from "@app/scene/reports/components/MileageTracking/components/preview";
import { RootState } from "@app/store/rootReducer";
import MileageTrackingCardConfiguration from "@app/types/dashboard/cards/mileageTracking/MileageTrackingCardConfiguration";
import Scope from "@app/types/dashboard/Scope";

import messages from "../../messages";

interface Props extends Pick<DrillDownModalProps, "onEdit" | "onHide"> {
  configuration: MileageTrackingCardConfiguration;
  customDateRange?: DateRange;
  onExportDrillDownData: typeof exportMileageTrackingCardDrillDownData;
  onFetchDrillDownData: (payload: DrillDownListFetcherPayload<Trip>) => void;
  onUpdateTrip: typeof updateTrip.request;
  scope: Scope;
  totalFilteredRecords: number;
  viewState: ViewState;
}

const DrillDown: FC<Props> = ({
  configuration,
  customDateRange,
  onEdit,
  onExportDrillDownData,
  onFetchDrillDownData,
  onHide,
  onUpdateTrip,
  scope,
  totalFilteredRecords,
  viewState,
}) => {
  const intl = useIntl();

  const handleExport = useCallback(() => {
    onExportDrillDownData({ configuration, scope });
  }, [onExportDrillDownData, configuration, scope]);

  const [selectedTripId, setSelectedTripId] = useState<Trip["id"] | undefined>();
  const handleRecordView = useCallback((entity: AnyEntity) => {
    setSelectedTripId((entity as Trip).id);
  }, []);
  const handleHidePreview = useCallback(() => setSelectedTripId(undefined), []);

  return (
    <ExtendedAnalytics keyPath="Mileage Tracking Drilldown">
      <DrillDownModal
        context={{
          onSaveNote: onUpdateTrip,
        }}
        entityType={EntityType.TRIP}
        gridFrameworkComponents={{
          editableNoteCellRenderer: EditableNoteCellRenderer,
          tripDateCellRenderer: TripDateCellRenderer,
          tripLocationCellRenderer: TripLocationCellRenderer,
          tripTypeCellRenderer: TripTypeCellRenderer,
          userChipCellRenderer: UserChipCellRenderer,
        }}
        onEdit={onEdit}
        onExportDrillDownData={handleExport}
        onFetchDrillDownData={onFetchDrillDownData}
        onHide={onHide}
        onRecordView={handleRecordView}
        subtitle={formatDrillDownDateRange(
          customDateRange ??
            getDatesRange(configuration.dateRange.range, configuration.dateRange.subRange)
        )}
        title={
          configuration.customTitle?.trim() ||
          intl.formatMessage(messages.cardTitle, {
            date: (text: ReactNode[]) => text,
            dateRange: configuration.dateRange.range,
            dateSubRange: configuration.dateRange.subRange,
          })
        }
        totalFilteredRecords={totalFilteredRecords}
        viewState={viewState}
      />
      {selectedTripId && (
        <ExtendedAnalytics keyPath="Drive Record Panel">
          <TripPreview
            onHide={handleHidePreview}
            tripId={selectedTripId}
            // to be higher than the drill-down modal
            zIndex={1001}
          />
        </ExtendedAnalytics>
      )}
    </ExtendedAnalytics>
  );
};

const mapStateToProps = (state: RootState) => ({
  customDateRange: getMileageTrackingDrillDownCustomDateRange(state),
  loading: isMileageTrackingDrillDownLoading(state),
  totalFilteredRecords: getMileageTrackingDrillDownTotalFilteredRecords(state),
  viewState: getMileageTrackingDrillDownViewState(state),
});

const mapDispatchToProps = {
  onExportDrillDownData: exportMileageTrackingCardDrillDownData,
  onFetchDrillDownData: fetchMileageTrackingCardDrillDownData.request,
  onUpdateTrip: updateTrip.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(DrillDown);
